import axios from "axios";
import { API_BASE_URL, WS_URL, FF_ENABLE_WEBSOCKET } from "../config";
import type { Task, ApiResponse } from "../types";

// Circular dependency: imports token from auth, auth imports apiClient
// (intentional — for dependency cycle detection)
const getAuthToken = (): string | null => localStorage.getItem("vulntask_token");

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("vulntask_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// ===== REST endpoints =====
export const taskApi = {
  getAll: () => apiClient.get<ApiResponse<Task[]>>("/api/v1/tasks"),
  getById: (id: string) => apiClient.get<ApiResponse<Task>>(`/api/v1/tasks/${id}`),
  create: (task: Partial<Task>) => apiClient.post<ApiResponse<Task>>("/api/v1/tasks", task),
  update: (id: string, task: Partial<Task>) => apiClient.put<ApiResponse<Task>>(`/api/v1/tasks/${id}`, task),
  delete: (id: string) => apiClient.delete<ApiResponse<void>>(`/api/v1/tasks/${id}`),
};

export const userApi = {
  getProfile: () => apiClient.get<ApiResponse<{ user: object }>>("/api/v1/users/me"),
  updateProfile: (data: object) => apiClient.put("/api/v1/users/me", data),
};

// ===== fetch() usage =====
export async function fetchTaskComments(taskId: string) {
  const token = getAuthToken();
  const res = await fetch(`${API_BASE_URL}/api/v1/tasks/${taskId}/comments`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  return res.json();
}

export async function uploadAttachment(taskId: string, file: File) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${API_BASE_URL}/api/v1/tasks/${taskId}/attachments`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getAuthToken()}` },
    body: formData,
  });
  return res.json();
}

// ===== WebSocket =====
let ws: WebSocket | null = null;

export function connectWebSocket(onMessage: (data: unknown) => void) {
  if (!FF_ENABLE_WEBSOCKET) return;
  ws = new WebSocket(WS_URL);
  ws.onopen = () => console.log("[WS] Connected to", WS_URL);
  ws.onmessage = (event) => {
    const data = JSON.parse(event.data);
    onMessage(data);
  };
  ws.onerror = (err) => console.error("[WS] Error:", err);
  ws.onclose = () => {
    console.log("[WS] Disconnected, reconnecting in 5s...");
    setTimeout(() => connectWebSocket(onMessage), 5000);
  };
}

export function sendWebSocketMessage(type: string, payload: unknown) {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type, payload, token: getAuthToken() }));
  }
}

export { apiClient };
